<template>
  <div class="w-64 bg-white border-r border-gray-200 h-full overflow-y-auto">
    <div class="p-4">
      <h2 class="text-lg font-semibold text-gray-900 mb-4">Documentation</h2>
      
      <nav class="space-y-1">
        <div v-for="section in sections" :key="section.id">
          <a
            :href="`#${section.id}`"
            class="flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors"
            :class="[
              activeSection === section.id
                ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-500'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            ]"
            @click.prevent="scrollToSection(section.id)"
          >
            <component :is="section.icon" class="mr-3 h-4 w-4" />
            {{ section.title }}
          </a>
          
          <!-- Subsections -->
          <div v-if="section.subsections && activeSection === section.id" class="ml-7 mt-1 space-y-1">
            <a
              v-for="subsection in section.subsections"
              :key="subsection.id"
              :href="`#${subsection.id}`"
              class="block px-3 py-1 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors"
              @click.prevent="scrollToSection(subsection.id)"
            >
              {{ subsection.title }}
            </a>
          </div>
        </div>
      </nav>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

// Icons (простые SVG компоненты)
const BookOpenIcon = {
  template: `
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253">
      </path>
    </svg>
  `
}

const KeyIcon = {
  template: `
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
            d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z">
      </path>
    </svg>
  `
}

const CodeIcon = {
  template: `
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
            d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4">
      </path>
    </svg>
  `
}

const ExclamationTriangleIcon = {
  template: `
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-1.866-.833-2.598 0L4.266 16.5c-.77.833.192 2.5 1.732 2.5z">
      </path>
    </svg>
  `
}

const QuestionMarkCircleIcon = {
  template: `
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
            d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z">
      </path>
    </svg>
  `
}

export interface DocSection {
  id: string
  title: string
  icon: any
  subsections?: { id: string; title: string }[]
}

const sections: DocSection[] = [
  {
    id: 'introduction',
    title: 'Introduction',
    icon: BookOpenIcon
  },
  {
    id: 'authentication',
    title: 'Authentication',
    icon: KeyIcon,
    subsections: [
      { id: 'api-keys', title: 'API Keys' },
      { id: 'making-requests', title: 'Making Requests' }
    ]
  },
  {
    id: 'endpoints',
    title: 'Endpoints',
    icon: CodeIcon,
    subsections: [
      { id: 'chat-completions', title: 'Chat Completions' },
      { id: 'models-list', title: 'List Models' },
      { id: 'models-info', title: 'Model Information' }
    ]
  },
  {
    id: 'examples',
    title: 'Code Examples',
    icon: CodeIcon,
    subsections: [
      { id: 'curl-examples', title: 'cURL Examples' },
      { id: 'javascript-examples', title: 'JavaScript' },
      { id: 'python-examples', title: 'Python' }
    ]
  },
  {
    id: 'error-handling',
    title: 'Error Handling',
    icon: ExclamationTriangleIcon
  },
  {
    id: 'faq',
    title: 'FAQ',
    icon: QuestionMarkCircleIcon
  }
]

const activeSection = ref<string>('introduction')

function scrollToSection(sectionId: string) {
  const element = document.getElementById(sectionId)
  if (element) {
    element.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    })
    activeSection.value = sectionId
  }
}

// Track scroll position to highlight active section
function updateActiveSection() {
  const scrollPosition = window.scrollY + 100 // Offset for header
  
  for (const section of sections) {
    const element = document.getElementById(section.id)
    if (element) {
      const { offsetTop, offsetHeight } = element
      if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
        activeSection.value = section.id
        break
      }
    }
    
    // Check subsections
    if (section.subsections) {
      for (const subsection of section.subsections) {
        const subsectionElement = document.getElementById(subsection.id)
        if (subsectionElement) {
          const { offsetTop, offsetHeight } = subsectionElement
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            activeSection.value = section.id
            break
          }
        }
      }
    }
  }
}

onMounted(() => {
  window.addEventListener('scroll', updateActiveSection)
  updateActiveSection()
})

onUnmounted(() => {
  window.removeEventListener('scroll', updateActiveSection)
})
</script> 